/**
 * RESSURSER, TILDELINGER OG KONFLIKTER — kontrakten (spesifikasjonen §4.1–4.3 og §5, trinn 1).
 *
 * ================================================================================================
 * DENNE FILA EIER TYPENE DRIZZLE IKKE KAN EIE.
 *
 * `resource_types.fields` og `resources.field_values` er `jsonb`. Drizzle typer en `jsonb()` som
 * `unknown` uten `$type<>()`, og det finnes ingen `$type<>` i skjemaet i dag (målt: null treff i
 * `packages/db/src/schema/`). Så definisjonen av hva som får ligge i de to blobbene bor her, og
 * API-et parser BEGGE veier — inn fra klienten og ut av basen.
 *
 * Det er den samme disiplinen `signup_answers` allerede har: «The type discipline lives on the
 * FIELD ... and the API validates the answer against it.» Forskjellen er at her er det ikke bare
 * verdien som er data — det er hele feltlista, og dermed hele typen.
 *
 * ================================================================================================
 * INGEN SETNING I KONFLIKTSVARET, BARE REGELEN OG TALLENE.
 *
 * §5 ber om et `rule_text` med ord: «2 tildelinger, én om gangen», «22 av 20 stk». CLAUDE.md
 * forbyr det: «The API never returns a translated sentence.» En setning fra basen ville vært norsk
 * i et produkt som svarer på to språk.
 *
 * Svaret bærer derfor `rule` pluss `used`, `capacity` og `unit` — nøyaktig det de to setningene
 * trenger — og `resources.conflict.<rule>` i språkfilene setter dem sammen. Samme utfall, riktig
 * sted.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { writeWarningsField } from './write-warnings.js';

import { personSystemKeySchema } from './person-standard-fields.js';
import { rowViewerSchema } from './row-viewer.js';

/**
 * ET PERSONFELT TYPEN SUMMERER (0921): feltets id, eller standardfeltets nøkkel («allergens_diets»),
 * som API-et slår opp til organisasjonens id. Aldri en egen nøkkel: de fulgte stavemåten (QA13 #76).
 */
export const summarizedPersonFieldRefSchema = z.union([z.uuid(), personSystemKeySchema]);

// ----------------------------------------------------------------------------- felles vokabular

/**
 * Ikonene en ressurstype kan velges blant (mockupens skjerm 3, steg 1).
 *
 * ET NAVN OG ALDRI EN SVG. Prinsipp 9 sier at typen er data — men ikonet er ikke data på den samme
 * måten: en opplastet SVG er kjørbar kode i et `<img>`, og et emoji tegner forskjellig på hver
 * maskin. Det som lagres er et NAVN frontenden slår opp, og det har ingen kjørbar form.
 *
 * ================================================================================================
 * TI ORD BLE TIL TI ORD PLUSS HELE LUCIDE (#22b, QA-runde 12).
 *
 * Atle: «Ikonbiblioteket må bli langt større enn 8 valg.» De ti nedenfor var vårt eget vokabular,
 * og de er for få: en festival som lager «Aggregat» eller «Gjerde» hadde ingenting å velge.
 * Appen tegner allerede med Lucide, så den nye formen er `lucide:truck` — ikonets navn, som tekst.
 *
 * DE TI BLIR STÅENDE, OG DET ER GRUNNEN TIL AT DET IKKE TRENGS EN MIGRERING. Kolonnen er `text`
 * uten `check`, radene som finnes har et av ordene, og de tegner som før. Å skrive dem om til
 * `lucide:car` ville byttet et ord festivalen forstår med et navn Lucide eier — og en migrering som
 * ikke kan angres er en dyr måte å rydde på.
 *
 * FORMEN VALIDERES, IKKE INNHOLDET. `lucide:truck` godtas, `lucide:Truck` og `lucide:../x` ikke.
 * Om Lucide faktisk HAR et ikon som heter `truck` er et spørsmål bare nettleseren kan svare på —
 * serveren har ikke ikonbiblioteket, og en liste på 1636 navn duplisert inn i kontrakten ville
 * vært den samme lista to steder, med to sjanser til å bli utdatert. Et navn som ikke finnes
 * tegner reserveglyfet, som er nøyaktig det `null` gjør.
 */
export const RESOURCE_ICONS = [
  'vehicle',
  'room',
  'meal',
  'stage',
  'equipment',
  'radio',
  'box',
  'ticket',
  'tool',
  'generic',
] as const;

/** De ti ordene som fantes før ikonbiblioteket. Fortsatt gyldige, fortsatt tegnet. */
export type LegacyResourceIcon = (typeof RESOURCE_ICONS)[number];

/** `lucide:truck`. Små bokstaver og bindestreker — Lucides egen navneform. */
export type LucideIconRef = `lucide:${string}`;

export type ResourceIcon = LegacyResourceIcon | LucideIconRef;

const LUCIDE_ICON_REF = /^lucide:[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isResourceIcon(value: string): value is ResourceIcon {
  return (RESOURCE_ICONS as readonly string[]).includes(value) || LUCIDE_ICON_REF.test(value);
}

export const resourceIconSchema: z.ZodType<ResourceIcon, string> = z
  .string()
  .trim()
  .max(64)
  .refine(isResourceIcon, { error: 'invalid_resource_icon' })
  .transform((value) => value as ResourceIcon);

/**
 * «HVOR MANGE KAN BRUKE DEN SAMTIDIG?» — spørsmål to i typebyggeren (sak 200/201, 0356).
 *
 *   `unlimited`  Så mange som vil. Teller ingenting; en bar bemannes, den bookes ikke.
 *   `exclusive`  Én om gangen, med valgfri størrelse («plass til 9 seter»).
 *   `shared`     Opptil et visst antall samtidig («2 senger»).
 *   `pool`       Vi har flere like, og holder styr på antallet («14 radioer»). Spilles som
 *                `shared`; forskjellen er ordet på skjermen og at utdeling på vakt teller stykker.
 */
export const CAPACITY_MODES = ['exclusive', 'shared', 'unlimited', 'pool'] as const;
export const capacityModeSchema = z.enum(CAPACITY_MODES);
export type CapacityMode = (typeof CAPACITY_MODES)[number];

/** Hvor en tildeling kom fra (§6). Bare `manual` har en skrivebane i trinn 1. */
export const ALLOCATION_SOURCES = [
  'travel',
  'accommodation',
  'hospitality',
  'shift',
  'program_item',
  'task',
  'manual',
  /* 0356: skrevet av en regel på ressursen, fra vaktplanen. */
  'rule',
  /* 0938: hendelsen i kalenderen som står på ressursen (QA13 #82). */
  'calendar_event',
] as const;
export const allocationSourceSchema = z.enum(ALLOCATION_SOURCES);

/** Hvem som skrev tildelingen (0357). Se `allocations.origin`. */
/** `base` (0447): fra salens grunnoppsett, lagt på et arrangement hos spillestedet. */
export const ALLOCATION_ORIGINS = ['manual', 'hand_out', 'rule', 'base'] as const;
export const allocationOriginSchema = z.enum(ALLOCATION_ORIGINS);
export type AllocationOrigin = (typeof ALLOCATION_ORIGINS)[number];

/** «Hvordan bookes den?» — klokkeslett, hele dager eller natt for natt. */
export const BOOKING_UNITS = ['clock', 'day', 'night'] as const;
export const bookingUnitSchema = z.enum(BOOKING_UNITS);
export type BookingUnit = (typeof BOOKING_UNITS)[number];

/** «Trenger den folk på jobb?» — nei, når den er åpen, eller hver gang den brukes. */
export const STAFF_MODES = ['none', 'open', 'in_use'] as const;
export const staffModeSchema = z.enum(STAFF_MODES);
export type StaffMode = (typeof STAFF_MODES)[number];

/** «Får folk den utdelt når de har vakt?» — nei, én til hver på vakta, eller én til vakta. */
export const HAND_OUT_MODES = ['none', 'person', 'shift'] as const;
export const handOutModeSchema = z.enum(HAND_OUT_MODES);
export type HandOutMode = (typeof HAND_OUT_MODES)[number];

/** Et klokkeslett uten dato og sone, «HH:MM». Leses i årgangens sone av tjenesten. */
export const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u, 'Use HH:MM.');

/** En post en ny ressurs av typen får (spørsmål én). */
export const defaultPostSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    needed: z.number().int().min(1).max(500).default(1),
    /** Personfeltet den som står her må ha — nøkkelen fra «Personfelt og roller». */
    requiredFieldKey: z.string().trim().min(1).max(60).nullish(),
  })
  .meta({ id: 'DefaultPost' });
export type DefaultPost = z.infer<typeof defaultPostSchema>;

/**
 * EN REGEL SOM FYLLER RESSURSEN FRA VAKTPLANEN (spørsmål seks).
 *
 * «Alle med vakt som starter mellom 14:00 og 18:00 får 1 porsjon, 16:00–17:30.» Tjenesten skriver
 * én tildeling per dag med de menneskene regelen traff, og skriver den om når vaktplanen endres.
 */
export const fillRuleSchema = z
  .object({
    startsFrom: clockTimeSchema,
    startsTo: clockTimeSchema,
    quantity: z.number().int().min(1).max(100).default(1),
    at: clockTimeSchema,
    until: clockTimeSchema,
  })
  .meta({ id: 'FillRule' });
export type FillRule = z.infer<typeof fillRuleSchema>;
export const MAX_FILL_RULES = 10;

/** «Per hva» en kostnad regnes. `hour` er timene i reservasjonene (276.6, 1063). */
export const COST_PER = ['day', 'use', 'total', 'hour'] as const;
export const costPerSchema = z.enum(COST_PER);
export type CostPer = (typeof COST_PER)[number];
export type AllocationSource = (typeof ALLOCATION_SOURCES)[number];

export const ALLOCATION_STATUSES = ['planned', 'done', 'cancelled'] as const;
export const allocationStatusSchema = z.enum(ALLOCATION_STATUSES);
export type AllocationStatus = (typeof ALLOCATION_STATUSES)[number];

/**
 * Felttypene en festival kan legge på en ressurstype (§4.1).
 *
 * `cost` og `document` står i spesifikasjonen og er med i lista, men trinn 1 tegner dem som tall og
 * tekst: den første trenger `budget_category_id` fra Økonomi, den andre en opplasting. Å ha dem i
 * enumet nå koster ingenting; å legge dem til senere er en migrasjon av hver eneste lagrede
 * definisjon.
 */
export const RESOURCE_FIELD_TYPES = [
  'text',
  'number',
  'date',
  'boolean',
  'select',
  'person',
  'cost',
  'document',
] as const;
export const resourceFieldTypeSchema = z.enum(RESOURCE_FIELD_TYPES);
export type ResourceFieldType = (typeof RESOURCE_FIELD_TYPES)[number];

/** Nøkkelen et felt adresseres med i `field_values`. Samme form som en avdelings `key`. */
const fieldKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9_-]{0,38}$/u);

/** Et felt festivalen fant på. `options` bare for `select`; tomt er ikke et valg. */
/** Én åpen dag (52): datoen i årgangen og tida. Stenger før den åpner er over midnatt (00:30). */
export const resourceOpenDaySchema = z
  .object({
    day: z.iso.date(),
    opensAt: clockTimeSchema,
    closesAt: clockTimeSchema,
  })
  .refine((value) => value.opensAt !== value.closesAt, {
    message: 'Opening and closing cannot be the same time.',
    path: ['closesAt'],
  })
  .meta({ id: 'ResourceOpenDay' });
export type ResourceOpenDay = z.infer<typeof resourceOpenDaySchema>;

/** Hele lista for én årgang, skrevet som en mengde (som beboerne på et rom). */
export const setResourceOpenDaysRequestSchema = z
  .object({ days: z.array(resourceOpenDaySchema).max(120) })
  .meta({ id: 'SetResourceOpenDaysRequest' });
export type SetResourceOpenDaysRequest = z.infer<typeof setResourceOpenDaysRequestSchema>;

export const resourceFieldSchema = z
  .object({
    key: fieldKeySchema,
    label: z.string().trim().min(1).max(60),
    type: resourceFieldTypeSchema,
    required: z.boolean().default(false),
    options: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
    /**
     * «VIS I ADVANCEN» (253.38, 0925). Verdien følger med til advance-raden, og byrået og artisten
     * ser den når typen er synlig for byrået. Av for nye felt; på for «Meny» i Spisested-malen.
     */
    showInAdvance: z.boolean().optional(),
  })
  .refine((field) => field.type !== 'select' || (field.options?.length ?? 0) > 0, {
    message: 'A select field must offer at least one option.',
    path: ['options'],
  })
  .meta({ id: 'ResourceField' });
export type ResourceField = z.infer<typeof resourceFieldSchema>;

/** Hvor mange felt en type kan ha. Tjue er «dette er et skjema», og et skjema er ikke en ressurs. */
export const MAX_RESOURCE_FIELDS = 20;

/**
 * En verdi i `field_values`.
 *
 * SKALARER OG INGENTING ANNET. `search_text` i basen flater blobben ut med
 * `jsonb_path_query_array(field_values, '$.*')`, og den ser bare ett nivå. En nøstet struktur
 * ville derfor vært usøkbar uten at noe sa fra — så den er ikke tillatt inn.
 */
export const resourceFieldValueSchema = z.union([z.string().max(500), z.number(), z.boolean()]);
export const resourceFieldValuesSchema = z.record(fieldKeySchema, resourceFieldValueSchema);
export type ResourceFieldValues = z.infer<typeof resourceFieldValuesSchema>;

// ------------------------------------------------------------------------------- ressurstyper

export const resourceTypeDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null = organisasjonens, delt av alle festivaler. Som en scene på org-nivå. */
    festivalId: z.uuid().nullable(),
    key: z.string(),
    name: z.string(),
    namePlural: z.string(),
    icon: resourceIconSchema.nullable(),

    capacityMode: capacityModeSchema,
    /** `exclusive`: hvor mange den rommer. Null er «vi teller ikke». */
    size: z.number().int().nullable(),
    sizeUnit: z.string().nullable(),
    /** `shared`: hvor mange enheter den har. */
    capacity: z.number().int().nullable(),
    unit: z.string().nullable(),
    perNight: z.boolean(),

    fields: z.array(resourceFieldSchema),
    /** Personfeltenes id-er (0921). */
    summarizePersonFields: z.array(z.uuid()),
    contexts: z.array(allocationSourceSchema),
    requireOwner: z.boolean(),
    visibleToAgency: z.boolean(),

    sortOrder: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),
    /**
     * En type noen har begynt på og ikke fullført (QA13 runde 2, #31b).
     *
     * Den står i innstillingstabellen merket KLADD, med «Fortsett» og «Slett kladd», og den kan
     * ikke velges når en ressurs opprettes. Forskjellig fra arkivert: en arkivert type er ferdig
     * og ute av bruk, en kladd har aldri vært i bruk.
     */
    draft: z.boolean(),
    /** Hvor mange ressurser typen bærer. Arkivering spør om dette tallet (analysen §3.6). */
    resourceCount: z.number().int(),

    /* De sju spørsmålene (0357). */
    bookingUnit: bookingUnitSchema,
    staffMode: staffModeSchema,
    opensAt: clockTimeSchema.nullable(),
    closesAt: clockTimeSchema.nullable(),
    defaultPosts: z.array(defaultPostSchema),
    /** Null = ingen blir satt på den. Ellers hva de kalles: «Passasjerer». */
    usersLabel: z.string().nullable(),
    handOut: handOutModeSchema,
    fillsFromShifts: z.boolean(),
    defaultFillRule: fillRuleSchema.nullable(),
    /**
     * «STANDARD VARIGHET» (256.6, 0980), i minutter: hvor lenge en reservasjon av typen varer når den
     * bookes fra et klokkeslett — 90 for en sitting. Null er «ingen standard». Kan endres (regel 7).
     */
    defaultMinutes: z.number().int().nullable().optional(),
  })
  .meta({ id: 'ResourceType' });
export type ResourceTypeDto = z.infer<typeof resourceTypeDtoSchema>;

export const RESOURCE_TYPE_SORT_KEYS = ['order'] as const;

export const listResourceTypesQuerySchema = cursorQuerySchema(RESOURCE_TYPE_SORT_KEYS)
  .extend({
    /** Festivalen typene skal gjelde for. Svaret er festivalens EGNE pluss organisasjonens. */
    festivalId: z.uuid().optional(),
    /** Arkiverte er ute som standard: velgerne skal ikke tilby dem (analysen §3.6). */
    includeArchived: z.stringbool().default(false),
  })
  .meta({ id: 'ListResourceTypesQuery' });
export type ListResourceTypesQuery = z.infer<typeof listResourceTypesQuerySchema>;

export const resourceTypeListResponseSchema = page(
  resourceTypeDtoSchema,
  'ResourceTypeListResponse',
);
export type ResourceTypeListResponse = z.infer<typeof resourceTypeListResponseSchema>;

/**
 * KAPASITETSREGELEN VALIDERES HER, ikke bare i basen.
 *
 * Skrankene i 0198 er det siste ordet, men en 23514 er en 500 foran noen som valgte «Deles» og
 * glemte kapasiteten. `superRefine` gir den samme regelen som et feltnivå-avslag veiviseren kan
 * peke på.
 */
const capacityShape = (
  value: {
    capacityMode: CapacityMode;
    bookingUnit?: BookingUnit | undefined;
    size?: number | null | undefined;
    sizeUnit?: string | null | undefined;
    capacity?: number | null | undefined;
    unit?: string | null | undefined;
    perNight?: boolean | undefined;
  },
  ctx: z.RefinementCtx,
): void => {
  if (value.capacityMode === 'unlimited') {
    if (value.capacity != null || value.size != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['capacity'],
        message: 'A type anyone may use counts nothing.',
      });
    }
    if (value.bookingUnit === 'night' || value.perNight === true) {
      ctx.addIssue({
        code: 'custom',
        path: ['bookingUnit'],
        message: 'Nothing is counted per night on a type anyone may use.',
      });
    }
    return;
  }
  /*
   * EN DELT TYPE KAN STÅ UTEN EGET TALL (QA15-S B9, Del 0 #15, 1237): antallet føres på ressursen
   * («Shure SM58: 40»). Et tall på typen er fortsatt lov, og er da det ressursene har uten sitt eget.
   */
  if (value.capacityMode === 'shared' || value.capacityMode === 'pool') {
    if (value.size != null) {
      ctx.addIssue({ code: 'custom', path: ['size'], message: 'A shared type has no size.' });
    }
    return;
  }
  if (value.capacity != null) {
    ctx.addIssue({
      code: 'custom',
      path: ['capacity'],
      message: 'An exclusive type has no capacity.',
    });
  }
  if (value.sizeUnit != null && value.size == null) {
    ctx.addIssue({ code: 'custom', path: ['size'], message: 'A unit needs a size.' });
  }
};

/** Standard varighet i minutter (0980): fem minutter til et døgn, som skranken i basen. */
const resourceDefaultMinutesSchema = z.number().int().min(5).max(1440);

export const createResourceTypeRequestSchema = z
  .object({
    /** Null er organisasjonens, sagt høyt — samme form som `createDepartmentRequest.editionId`. */
    festivalId: z.uuid().nullable(),
    name: z.string().trim().min(1).max(60),
    namePlural: z.string().trim().min(1).max(60),
    icon: resourceIconSchema.nullish(),
    capacityMode: capacityModeSchema,
    size: z.number().int().min(1).max(100_000).nullish(),
    sizeUnit: z.string().trim().min(1).max(30).nullish(),
    capacity: z.number().int().min(1).max(100_000).nullish(),
    unit: z.string().trim().min(1).max(30).nullish(),
    perNight: z.boolean().default(false),
    fields: z.array(resourceFieldSchema).max(MAX_RESOURCE_FIELDS).default([]),
    summarizePersonFields: z
      .array(summarizedPersonFieldRefSchema)
      .max(MAX_RESOURCE_FIELDS)
      .default([]),
    contexts: z.array(allocationSourceSchema).min(1).default(['manual']),
    requireOwner: z.boolean().default(false),
    visibleToAgency: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /** «Lagre utkast og avslutt» sender `true`. Standarden er en ferdig type (#31b). */
    draft: z.boolean().default(false),

    /* De sju spørsmålene (0357). `perNight` over følger `bookingUnit` når den sendes. */
    bookingUnit: bookingUnitSchema.optional(),
    staffMode: staffModeSchema.default('none'),
    opensAt: clockTimeSchema.nullish(),
    closesAt: clockTimeSchema.nullish(),
    defaultPosts: z.array(defaultPostSchema).max(20).default([]),
    usersLabel: z.string().trim().min(1).max(60).nullish(),
    handOut: handOutModeSchema.default('none'),
    fillsFromShifts: z.boolean().default(false),
    defaultFillRule: fillRuleSchema.nullish(),
    defaultMinutes: resourceDefaultMinutesSchema.nullish(),
  })
  .superRefine(capacityShape)
  .meta({ id: 'CreateResourceTypeRequest' });
export type CreateResourceTypeRequest = z.infer<typeof createResourceTypeRequestSchema>;

/**
 * `key` og `festivalId` er fraværende med vilje.
 *
 * Slugen genereres av det første navnet og flytter seg aldri — forslagslista og en framtidig import
 * adresserer typen på den. Nivået er ikke en rettelse heller: å flytte en type mellom festival og
 * organisasjon er å flytte hver ressurs den bærer, og det er en egen handling med sin egen
 * advarsel, ikke et felt i et skjema.
 */
export const updateResourceTypeRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    namePlural: z.string().trim().min(1).max(60).optional(),
    icon: resourceIconSchema.nullish(),
    capacityMode: capacityModeSchema.optional(),
    size: z.number().int().min(1).max(100_000).nullish(),
    sizeUnit: z.string().trim().min(1).max(30).nullish(),
    capacity: z.number().int().min(1).max(100_000).nullish(),
    unit: z.string().trim().min(1).max(30).nullish(),
    perNight: z.boolean().optional(),
    fields: z.array(resourceFieldSchema).max(MAX_RESOURCE_FIELDS).optional(),
    summarizePersonFields: z
      .array(summarizedPersonFieldRefSchema)
      .max(MAX_RESOURCE_FIELDS)
      .optional(),
    contexts: z.array(allocationSourceSchema).min(1).optional(),
    requireOwner: z.boolean().optional(),
    visibleToAgency: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /** `true` arkiverer, `false` henter tilbake. Ikke en sletting — se analysen §3.6. */
    archived: z.boolean().optional(),
    /** `false` fullfører kladden. «Fortsett» i tabellen ender her (#31b). */
    draft: z.boolean().optional(),

    bookingUnit: bookingUnitSchema.optional(),
    staffMode: staffModeSchema.optional(),
    opensAt: clockTimeSchema.nullish(),
    closesAt: clockTimeSchema.nullish(),
    defaultPosts: z.array(defaultPostSchema).max(20).optional(),
    usersLabel: z.string().trim().min(1).max(60).nullish(),
    handOut: handOutModeSchema.optional(),
    fillsFromShifts: z.boolean().optional(),
    defaultFillRule: fillRuleSchema.nullish(),
    defaultMinutes: resourceDefaultMinutesSchema.nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateResourceTypeRequest' });
export type UpdateResourceTypeRequest = z.infer<typeof updateResourceTypeRequestSchema>;

// ----------------------------------------------------------------------------------- ressurser

export const resourceDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid().nullable(),
    resourceTypeId: z.uuid(),
    /** Typens navn og regel, med på raden: lista tegner «Type» uten et andre kall. */
    typeName: z.string(),
    typeIcon: resourceIconSchema.nullable(),
    capacityMode: capacityModeSchema,
    perNight: z.boolean(),
    /** Den gjeldende kapasiteten: ressursens overstyring om den finnes, ellers typens. */
    effectiveCapacity: z.number().int().nullable(),
    unit: z.string().nullable(),
    size: z.number().int().nullable(),
    sizeUnit: z.string().nullable(),

    name: z.string(),
    subtitle: z.string().nullable(),
    capacityOverride: z.number().int().nullable(),
    fieldValues: resourceFieldValuesSchema,
    ownerContactId: z.uuid().nullable(),
    ownerName: z.string().nullable(),
    notes: z.string().nullable(),
    /** Avdelingene i DENNE årgangen ressursen er merket med (avgjørelse 4B). */
    departmentIds: z.array(z.uuid()),
    /**
     * AVDELINGEN SOM EIER DEN I ÅR (0357) — den vaktene arver, og den lista sorterer etter.
     * Alltid én av `departmentIds`, eller null.
     */
    primaryDepartmentId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),

    /* Typens svar, med på raden så ressurssiden kan tegne fanene uten et andre kall. */
    bookingUnit: bookingUnitSchema,
    staffMode: staffModeSchema,
    usersLabel: z.string().nullable(),
    handOut: handOutModeSchema,
    fillsFromShifts: z.boolean(),
    /** Personfeltenes id-er (0921). */
    summarizePersonFields: z.array(z.uuid()),

    /** Åpningstiden som gjelder: ressursens egen om den har en, ellers typens. */
    opensAt: clockTimeSchema.nullable(),
    closesAt: clockTimeSchema.nullable(),
    /** Ressursens EGEN åpningstid — null når den følger typen. */
    ownOpensAt: clockTimeSchema.nullable(),
    ownClosesAt: clockTimeSchema.nullable(),
    /**
     * ÅPNINGSTID PER DAG I ÅRGANGEN (52, 0925). Tom: standardtiden over gjelder hver festivaldag,
     * som før. Har den dager, er de andre dagene stengt.
     */
    openDays: z.array(resourceOpenDaySchema).optional(),
    /**
     * «BRUKES I PROGRAMMET» (29): en scene peker på ressursen. Ressursen er stedets ene navn, og
     * scenens navn holdes likt med den (0925).
     */
    inProgramme: z.boolean().optional(),
    /**
     * Scenen i årgangen som peker på ressursen (259.9), så «Brukes i programmet» kan slås av fra
     * ressurspanelet. Null når ingen gjør det.
     */
    programmeStageId: z.uuid().nullable().optional(),
    /**
     * Hvor scenen bor (A19): `edition` når årgangen har sin egen, `festival` når bare festivalens
     * standardsett har den (en scene lagt til uten årgang). Null uten scene.
     */
    programmeStageLevel: z.enum(['festival', 'edition']).nullable().optional(),
    /** Typens «standard varighet» i minutter (256.6, 0980). Null: ingen standard. */
    defaultMinutes: z.number().int().nullable().optional(),
    fillRules: z.array(fillRuleSchema),
    costMinor: z.number().int().nullable(),
    costPer: costPerSchema.nullable(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'Resource' });
export type ResourceDto = z.infer<typeof resourceDtoSchema>;

export const RESOURCE_SORT_KEYS = ['order', 'name'] as const;

export const listResourcesQuerySchema = cursorQuerySchema(RESOURCE_SORT_KEYS)
  .extend({
    /** Årgangen lista tegnes i. Avgjør hvilket avdelingstre merkingen leses mot. */
    editionId: z.uuid(),
    /**
     * Avdelingen i treet. Tar med seksjonene under, som filteret i vaktplanen (QA-funn 1).
     * `none` er «Uten avdeling» — innboksen treet viser sist.
     */
    departmentId: z.union([z.uuid(), z.literal('none')]).optional(),
    resourceTypeId: z.uuid().optional(),
    ownerContactId: z.uuid().optional(),
    /** Fritekst mot `search_text`. Gjelder ALLTID alle ressurser, uansett tre (§7.1). */
    q: z.string().trim().min(1).max(120).optional(),
    includeArchived: z.stringbool().default(false),
  })
  .meta({ id: 'ListResourcesQuery' });
export type ListResourcesQuery = z.infer<typeof listResourcesQuerySchema>;

export const resourceListResponseSchema = page(resourceDtoSchema, 'ResourceListResponse');
export type ResourceListResponse = z.infer<typeof resourceListResponseSchema>;

export const createResourceRequestSchema = z
  .object({
    resourceTypeId: z.uuid(),
    /** Null er organisasjonens. Påkrevd av samme grunn som på typen: det skal ikke kunne glemmes. */
    festivalId: z.uuid().nullable(),
    name: z.string().trim().min(1).max(120),
    subtitle: z.string().trim().min(1).max(160).nullish(),
    capacityOverride: z.number().int().min(1).max(100_000).nullish(),
    fieldValues: resourceFieldValuesSchema.default({}),
    ownerContactId: z.uuid().nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
    departmentIds: z.array(z.uuid()).max(20).default([]),
    /** Eieren i årgangen `editionId` i spørringen peker på. Legges også i `departmentIds`. */
    primaryDepartmentId: z.uuid().nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    opensAt: clockTimeSchema.nullish(),
    closesAt: clockTimeSchema.nullish(),
    fillRules: z.array(fillRuleSchema).max(MAX_FILL_RULES).optional(),
    costMinor: z.number().int().min(0).max(1_000_000_000_00).nullish(),
    costPer: costPerSchema.nullish(),
  })
  .meta({ id: 'CreateResourceRequest' });
export type CreateResourceRequest = z.infer<typeof createResourceRequestSchema>;

export const updateResourceRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    subtitle: z.string().trim().min(1).max(160).nullish(),
    capacityOverride: z.number().int().min(1).max(100_000).nullish(),
    fieldValues: resourceFieldValuesSchema.optional(),
    ownerContactId: z.uuid().nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
    departmentIds: z.array(z.uuid()).max(20).optional(),
    primaryDepartmentId: z.uuid().nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    archived: z.boolean().optional(),
    opensAt: clockTimeSchema.nullish(),
    closesAt: clockTimeSchema.nullish(),
    fillRules: z.array(fillRuleSchema).max(MAX_FILL_RULES).optional(),
    costMinor: z.number().int().min(0).max(1_000_000_000_00).nullish(),
    costPer: costPerSchema.nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateResourceRequest' });

/**
 * «LAG VAKTER» — én vakt per post på stedet per dag, fra åpningstiden (sak 200.2).
 *
 * Datoene er festivaldager i årgangens sone. Tjenesten regner klokkeslettene selv; en vakt som
 * finnes for posten den dagen lages ikke på nytt, så knappen kan trykkes to ganger.
 */
export const createResourceShiftsRequestSchema = z
  .object({
    editionId: z.uuid(),
    dates: z.array(z.iso.date()).min(1).max(60),
    /** Bare disse postene (postskuffen under Stab og vakter lager vakter for én). Uten: alle. */
    roleIds: z.array(z.uuid()).min(1).max(50).optional(),
  })
  .meta({ id: 'CreateResourceShiftsRequest' });
export type CreateResourceShiftsRequest = z.infer<typeof createResourceShiftsRequestSchema>;

export const createResourceShiftsResponseSchema = z
  .object({ created: z.number().int(), skipped: z.number().int() })
  .meta({ id: 'CreateResourceShiftsResponse' });
export type CreateResourceShiftsResponse = z.infer<typeof createResourceShiftsResponseSchema>;
export type UpdateResourceRequest = z.infer<typeof updateResourceRequestSchema>;

/*
 * KVITTERINGEN EN SLETTET RESSURS GIR (QA 23.09 punkt 4, «Angre» 23. september). Reservasjonene
 * ble slettet; postene og vaktene ble stående uten fast sted. Gjenopprettingen setter stedet
 * tilbake på nøyaktig de radene.
 */
export const resourceDeletionSchema = z
  .object({
    resourceId: z.uuid(),
    reservationIds: z.array(z.uuid()),
    postIds: z.array(z.uuid()),
    shiftIds: z.array(z.uuid()),
  })
  .meta({ id: 'ResourceDeletion' });
export type ResourceDeletion = z.infer<typeof resourceDeletionSchema>;

export const resourceTypeDeletionSchema = z
  .object({ resources: z.array(resourceDeletionSchema), typePostIds: z.array(z.uuid()) })
  .meta({ id: 'ResourceTypeDeletion' });
export type ResourceTypeDeletion = z.infer<typeof resourceTypeDeletionSchema>;

// --------------------------------------------------------------------------------- tildelinger

/** Hvor mange personer én reservasjon kan ha. Personvelgeren slutter å tilby flere (trinn 1 §3). */
export const MAX_ALLOCATION_PEOPLE = 200;

export const allocationPersonSchema = z
  .object({ contactId: z.uuid(), displayName: z.string() })
  .meta({ id: 'AllocationPerson' });

export const allocationDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    resourceId: z.uuid(),
    resourceName: z.string(),
    resourceTypeId: z.uuid(),
    typeName: z.string(),

    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** Satt bare for `per_night`-typer; da er de to over en projeksjon av denne (§11.6). */
    night: z.iso.date().nullable(),

    quantity: z.number().int().nullable(),
    title: z.string(),
    sourceType: allocationSourceSchema,
    sourceId: z.uuid().nullable(),
    /** Hvem som skrev raden: et menneske, utdelingen på en post, eller en regel (0357). */
    origin: allocationOriginSchema,
    /**
     * KILDEN SOM ET NAVN (rettelsesordren B4, QA-funn 5 fra trinn 2).
     *
     * «Kvitbjørn» for en reise eller en middag, «Scandic Nidelven» for et rom, skiftets tittel for
     * et skift. Null for `manual`, som ikke har en kilde — og det er riktig, ikke en mangel.
     *
     * `sourceType` og `sourceId` har vært i DTO-en siden trinn 1 og ingen skjerm leste dem: den
     * som fant en bil booket 18–19 kunne ikke se at det var Kvitbjørns henting. Navnet ligger et
     * annet sted per kildetype, og det var grunnen til at det ventet — se `SOURCE_LABEL`.
     */
    sourceLabel: z.string().nullable(),
    /** Programposten kilden hører til, for lenka. Null for et skift og for `manual`. */
    sourcePerformanceId: z.uuid().nullable(),
    departmentId: z.uuid().nullable(),
    ownerContactId: z.uuid().nullable(),
    note: z.string().nullable(),
    status: allocationStatusSchema,
    people: z.array(allocationPersonSchema),
    /**
     * HVOR `people` KOMMER FRA (grep C2, 0925). `own` er tildelingens egen liste og kan redigeres.
     * `travel`, `stay` og `dinner` er lest fra kilden — reisens reisende, rommets beboere, de som
     * spiser — og endres der, ikke her.
     */
    peopleFrom: z.enum(['own', 'travel', 'stay', 'dinner']).optional(),
    acknowledgedAt: z.iso.datetime().nullable(),
    /** Når raden sist ble endret. «Avklart» betyr kvittert ETTER dette (§5). */
    updatedAt: z.iso.datetime(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
    /** Konflikter og «passer ikke» etter skrivingen (§8.1 #6): bare i svaret på `POST /allocations`. */
    warnings: writeWarningsField,
  })
  .meta({ id: 'Allocation' });
export type AllocationDto = z.infer<typeof allocationDtoSchema>;

export const ALLOCATION_SORT_KEYS = ['starts'] as const;

export const listAllocationsQuerySchema = cursorQuerySchema(ALLOCATION_SORT_KEYS)
  .extend({
    editionId: z.uuid(),
    /** Vinduet. Uke- og dagvisningen sender sitt eget; panelet sender ressursens. */
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    resourceId: z.uuid().optional(),
    departmentId: z.union([z.uuid(), z.literal('none')]).optional(),
    /**
     * KILDEN (rettelse A2). «Hvilke tildelinger hører til denne oppgaven» er spørsmålet
     * oppgavepanelet stiller for å tegne lista med «Løsne».
     *
     * De to er uavhengige med vilje: `sourceType` alene svarer «alt som kom fra en oppgave».
     */
    sourceType: allocationSourceSchema.optional(),
    sourceId: z.uuid().optional(),
  })
  .meta({ id: 'ListAllocationsQuery' });
export type ListAllocationsQuery = z.infer<typeof listAllocationsQuerySchema>;

export const allocationListResponseSchema = page(allocationDtoSchema, 'AllocationListResponse');
export type AllocationListResponse = z.infer<typeof allocationListResponseSchema>;

export const createAllocationRequestSchema = z
  .object({
    editionId: z.uuid(),
    resourceId: z.uuid(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** Sendes av per-natt-typer. Tjenesten skriver projeksjonen; den kan ikke sendes inn. */
    night: z.iso.date().nullish(),
    quantity: z.number().int().min(1).max(100_000).nullish(),
    title: z.string().trim().min(1).max(160),
    sourceType: allocationSourceSchema.default('manual'),
    sourceId: z.uuid().nullish(),
    departmentId: z.uuid().nullish(),
    ownerContactId: z.uuid().nullish(),
    note: z.string().trim().min(1).max(2000).nullish(),
    status: allocationStatusSchema.default('planned'),
    contactIds: z.array(z.uuid()).max(MAX_ALLOCATION_PEOPLE).default([]),
  })
  .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: 'The end must come after the start.',
    path: ['endsAt'],
  })
  .meta({ id: 'CreateAllocationRequest' });
export type CreateAllocationRequest = z.infer<typeof createAllocationRequestSchema>;

/**
 * `resourceId` er fraværende: å flytte en tildeling til en annen ressurs er å avlyse den ene og
 * lage den andre. Den ville tatt folkene med seg, og konflikten den løste ville blitt stående
 * avklart på en rad som ikke lenger er i den.
 */
export const updateAllocationRequestSchema = z
  .object({
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    night: z.iso.date().nullish(),
    quantity: z.number().int().min(1).max(100_000).nullish(),
    title: z.string().trim().min(1).max(160).optional(),
    departmentId: z.uuid().nullish(),
    ownerContactId: z.uuid().nullish(),
    note: z.string().trim().min(1).max(2000).nullish(),
    status: allocationStatusSchema.optional(),
    contactIds: z.array(z.uuid()).max(MAX_ALLOCATION_PEOPLE).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateAllocationRequest' });
export type UpdateAllocationRequest = z.infer<typeof updateAllocationRequestSchema>;

// ----------------------------------------------------------------------------------- konflikter

/**
 * Hvilken regel som ble brutt. Se filhodet for hvorfor det er en kode og ikke en setning.
 *
 *   `exclusive_overlap`  to tildelinger på en ressurs som tar én om gangen
 *   `exclusive_size`     én tildeling som ber om flere enn ressursen rommer
 *   `shared_capacity`    summen av overlappende antall over kapasiteten
 *   `shared_night`       samme, men gruppert på natten framfor på klokka
 *   `person_double_booked` ETT MENNESKE på to ting som overlapper (trinn 5.2)
 *   `stage_double_booked`  TO PROGRAMPOSTER på samme scene (trinn 6.2)
 *   `stage_occupied`       en scenetid som OPPTAR SCENEN mens en annen act spiller eller rigger
 *                          («Load in Ari Bajgora mens GOLF spiller», 0924)
 *   `person_two_places`    ett menneske to steder samtidig PÅ TVERS AV LAGENE: sett, scenetid,
 *                          reise, middag, vakt, kalenderhendelse — med personens egen tid (0924)
 *
 * Den femte er den eneste som ikke handler om en ressurs, og DTO-en under sier det: `resourceId`
 * er null på den, `personId` er satt, og `shiftIds` bærer skiftene. En regel som hadde delt DTO
 * i to ville gitt «Kun konflikter» to lister å holde i takt.
 */
export const CONFLICT_RULES = [
  'exclusive_overlap',
  'exclusive_size',
  'shared_capacity',
  'shared_night',
  'person_double_booked',
  'stage_double_booked',
  'stage_occupied',
  'person_two_places',
] as const;
export const conflictRuleSchema = z.enum(CONFLICT_RULES);
export type ConflictRule = (typeof CONFLICT_RULES)[number];

export const allocationConflictDtoSchema = z
  .object({
    /** Null bare på `person_double_booked` — den konflikten er ikke på en ting. */
    resourceId: z.uuid().nullable(),
    resourceName: z.string(),
    /**
     * MENNESKET SOM STÅR TO STEDER (trinn 5.2), og navnet det leses under.
     *
     * Satt bare på `person_double_booked`. Navnet er med fordi en konfliktliste uten det er en
     * liste med uuid-er — og tjenesten holder det tilbake for en rolle uten `crew_member:list`,
     * av samme grunn sjåførvelgeren krever den cella. Se `AllocationsService.conflicts`.
     */
    personId: z.uuid().nullable(),
    personName: z.string().nullable(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    night: z.iso.date().nullable(),
    rule: conflictRuleSchema,
    used: z.number().int(),
    capacity: z.number().int().nullable(),
    unit: z.string().nullable(),
    allocationIds: z.array(z.uuid()),
    /** Skiftene som krangler. Tom på de fire ressursreglene; én eller to på den femte. */
    shiftIds: z.array(z.uuid()),
    /**
     * PROGRAMPOSTENE SOM KRANGLER (trinn 6.2).
     *
     * To på `stage_double_booked`. Én på `exclusive_overlap` når en lydsjekk er booket oppå en
     * konsert — og da står den andre id-en i `allocationIds`, fordi paret er én av hver.
     */
    performanceIds: z.array(z.uuid()),
    /**
     * HENDELSENE SOM KRANGLER, fra hendelseslaget (0924) — satt på `stage_occupied` og
     * `person_two_places`, tom ellers. Med tittelen og acten, så setningen kan si «Load in Ari
     * Bajgora» uten et oppslag til.
     */
    events: z.array(
      z.object({
        key: z.string(),
        kind: z.string(),
        title: z.string(),
        actName: z.string().nullable(),
      }),
    ),
    /** Sann når hver tildeling i konflikten er kvittert etter sin siste endring (§5). */
    acknowledged: z.boolean(),
  })
  .meta({ id: 'AllocationConflict' });
export type AllocationConflictDto = z.infer<typeof allocationConflictDtoSchema>;

export const listConflictsQuerySchema = z
  .object({
    editionId: z.uuid(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    departmentId: z.uuid().optional(),
  })
  .meta({ id: 'ListConflictsQuery' });
export type ListConflictsQuery = z.infer<typeof listConflictsQuerySchema>;

export const conflictListResponseSchema = z
  .object({ items: z.array(allocationConflictDtoSchema) })
  .meta({ id: 'ConflictListResponse' });
export type ConflictListResponse = z.infer<typeof conflictListResponseSchema>;

// ------------------------------------------------------------- tellerne i avdelingstreet (§3.5)

/**
 * FASTE TELLERE FOR ÅRGANGEN, IKKE FILTRERTE (avgjørelse 6A).
 *
 * Mockupens fot sier «Tallene følger filtrene over». Det ville betydd en aggregering over 500
 * ressurser ved hvert filterklikk, på en side som allerede har en liste og et rutenett å tegne.
 * Faste tall kan caches i `CACHE.reference`, som årgangens avdelingstre allerede bruker.
 */
export const resourceDepartmentSummaryRowSchema = z
  .object({
    /** Null er «Uten avdeling» — raden treet viser sist. */
    departmentId: z.uuid().nullable(),
    resources: z.number().int(),
    conflicts: z.number().int(),
  })
  .meta({ id: 'ResourceDepartmentSummaryRow' });

export const resourceDepartmentSummarySchema = z
  .object({
    items: z.array(resourceDepartmentSummaryRowSchema),
    /** Hele årgangen, som raden «Alle» viser. */
    totalResources: z.number().int(),
    totalConflicts: z.number().int(),
  })
  .meta({ id: 'ResourceDepartmentSummary' });
export type ResourceDepartmentSummary = z.infer<typeof resourceDepartmentSummarySchema>;

export const resourceDepartmentSummaryQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ResourceDepartmentSummaryQuery' });
export type ResourceDepartmentSummaryQuery = z.infer<typeof resourceDepartmentSummaryQuerySchema>;

// --------------------------------------------- tildelingen en KONTEKST bærer (§6, trinn 2 og 4)

/**
 * Tildelingen en reise, et rom eller en sitting er booket mot — som den ene linja skjermen viser.
 *
 * ================================================================================================
 * ÉN FORM FOR ALLE TRE KONTEKSTENE, OG DET ER AVGJØRELSEN.
 *
 * Den ble skrevet for reisen i trinn 2 og het `TravelAllocation`. Trinn 4 ga rommet og sittingen
 * det samme behovet, og tre like former ville vært tre steder å glemme det samme: at byrået leser
 * den samme raden.
 *
 * NAVN OG TID, ingenting mer. §6: «byrået skal se navn og tid på tildelinger som hører til deres
 * programpost (‹Bil 2 henter dere 16:00›), ikke festivalens felt og ikke resten av bilparken.»
 * Formen er derfor den samme for begge hus, og det er meningen: da finnes det ikke et felt noen
 * kan glemme å skjule. Reg.nr., romnummer og pris er ikke her, og kan ikke bli det ved et uhell.
 *
 * ================================================================================================
 * `night` ER NULL FOR ALT SOM IKKE ER EN NATT.
 *
 * En `per_night`-type bookes av NATTA, og `startsAt`/`endsAt` er projeksjonen fra typens standard
 * inn- og utsjekk (analysen §11.6). En henting har ingen natt, og feltet står som null — framfor at
 * reisen har én form og rommet en annen.
 */
export const contextAllocationSchema = z
  .object({
    id: z.uuid(),
    resourceId: z.uuid(),
    /** «Bil 2», «Rom 314». Ressursens navn, som er det man sier når man mener den. */
    resourceName: z.string(),
    /** «Kjøretøy», «Rom». Typens navn, så en rad kan leses uten å slå opp. */
    typeName: z.string(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** Satt bare for `per_night`-typer. Se filhodet. */
    night: z.iso.date().nullable(),
    /**
     * ALLE NETTENE RADEN HAR BOOKET, ikke bare den `night` bærer (rettelsesordren B1).
     *
     * Et opphold 15.–17. juli er to netter og derfor to tildelinger: `allocation_id` peker på den
     * første, og resten finnes gjennom `source_id`. Uten dette skrev rommet «15. juli» på et
     * opphold som varer til den 17. — QA-funn 4s egen setning.
     *
     * Tom for en reise og for en tildeling noen knyttet for hånd.
     */
    nights: z.array(z.iso.date()).default([]),
    /**
     * `hand_out` når posten delte den ut (0357). Vaktpanelet viser den under «Deles ut på vakta»
     * og uten «Løsne»: den følger folkene, ikke et valg noen tok.
     */
    origin: allocationOriginSchema.default('manual'),
    /** Antall, for en pool: «4 radioer». */
    quantity: z.number().int().nullable().default(null),
    /**
     * Tildelingen ligger i LESERENS hus (Atle, 22. september: koblingen lagres i huset som
     * knyttet). False er det andre husets kobling, synlig gjennom `visible_to_agency`: den kan
     * leses, ikke løsnes, og vi kan knytte vår egen ved siden av.
     */
    mine: z.boolean().default(true),
    /**
     * RESSURSEN VINNER OVER FRITEKSTEN (grep C2, 253.38). Feltene på ressursen typen har merket
     * «vis i advancen» (`showInAdvance`), med verdi — «Meny: Kjøttkaker i brun saus». Lest live fra
     * ressursen, så en endret meny står hos alle med en gang.
     */
    advanceFields: z
      .array(z.object({ key: z.string(), label: z.string(), value: z.string() }))
      .optional(),
    /** Ressursens ansvarlige, kontaktpersonen for stedet (109). */
    ownerName: z.string().nullable().optional(),
    ownerPhone: z.string().nullable().optional(),
  })
  .meta({ id: 'ContextAllocation' });
export type ContextAllocation = z.infer<typeof contextAllocationSchema>;

/**
 * «VELG KJØRETØY» / «VELG ROM» / «VELG SITTING» — én forespørsel som både booker og knytter.
 *
 * ================================================================================================
 * KONTEKSTEN SENDER EN RESSURS-ID, IKKE EN TILDELINGS-ID.
 *
 * §6, siste linje i tabellen: «Konteksten vet ikke hva den får tilbake — bare en ressurs-id.» Så
 * dette er ikke «knytt reisen til tildeling X»: det finnes ingen tildeling før noen trykker Bekreft
 * i bestillingsboksen, og å kreve to kall ville latt en bil bli booket på en reise som feilet
 * etterpå. Tjenesten oppretter tildelingen med riktig `source_type` og `source_id`, og setter
 * `allocation_id` i den samme runden.
 *
 * Feltene er bestillingsboksens egne (§7.5), og de er en DELMENGDE av `createAllocationRequest`:
 * `editionId` leses av raden, `sourceType`/`sourceId` settes av tjenesten, og `status` er alltid
 * `planned` — noe man nettopp valgte er planlagt, ikke bekreftet av noen andre enn en selv.
 *
 * ================================================================================================
 * ÉN FORM FOR TRE KONTEKSTER (trinn 4).
 *
 * Skrevet for reisen i trinn 2. Rommet og sittingen stiller nøyaktig samme spørsmål, og tre
 * kontrakter ville vært tre steder å glemme det samme — blant annet at `night` finnes.
 *
 * `night` ER FOR `per_night`-TYPER, og den er valgfri fordi de fleste kontekster ikke har en.
 * Sendes den, er NATTA sannheten og tjenesten regner `startsAt`/`endsAt` fra typens standard inn-
 * og utsjekk (analysen §11.6) — klienten skal ikke gjette hva et hotell kaller innsjekk.
 *
 * ================================================================================================
 * Å VELGE PÅ NYTT ER Å AVLYSE OG BOOKE, ikke å flytte.
 *
 * «Flytt … til Bil 3» sender denne på nytt. `updateAllocationRequestSchema` sier hvorfor det ikke
 * kan være en `PATCH` av `resourceId`: «å flytte en tildeling til en annen ressurs er å avlyse den
 * ene og lage den andre. Den ville tatt folkene med seg, og konflikten den løste ville blitt
 * stående avklart på en rad som ikke lenger er i den.» Tjenesten sletter derfor den forrige — men
 * bare om den var radens egen, aldri en noen laget for hånd på Ressurser-siden.
 */
export const linkContextAllocationRequestSchema = z
  .object({
    resourceId: z.uuid(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** Sendes av per-natt-typer. Tjenesten skriver projeksjonen; den kan ikke sendes inn. */
    night: z.iso.date().nullish(),
    /**
     * FLERE NETTER I ÉN BESTILLING (rettelsesordren B1, QA-funn 4).
     *
     * Et opphold 15.–17. juli er TO netter, og «Velg rom» bookte én. Natta 16.→17. sto ledig for
     * andre og konflikten den natta ble aldri vist — som er hele grunnen `per_night` finnes.
     *
     * ÉN TILDELING PER NATT, ikke én med et spenn. `allocations.night` er én dato fordi
     * `shared_night` grupperer PÅ den: en rad som dekket to netter ville telt mot kapasiteten den
     * første og vært usynlig den andre. Modellen bærer altså ikke «flere netter i én rad», og det
     * er riktig av den.
     *
     * Hver natt får sitt eget døgn regnet av de samme klokkeslettene `startsAt`/`endsAt` bærer, så
     * feltet er en LISTE OVER DATOER og ikke en liste over tidsrom. Utelates det, er oppførselen
     * den gamle: én natt, `night`.
     */
    nights: z.array(z.iso.date()).min(1).max(60).optional(),
    /** «Henting Katarina Barruk». Fri, fordi radens egne felt sjelden gir en lesbar setning. */
    title: z.string().trim().min(1).max(160),
    /** Antall reisende, for typer som deles. Null for en bil eller et rom som tas helt. */
    quantity: z.number().int().min(1).max(100_000).nullish(),
    departmentId: z.uuid().nullish(),
    note: z.string().trim().min(1).max(2000).nullish(),
    /** «Hvem» — sjåfør, beboere, spisende. Samme liste `allocation_people` bærer. */
    contactIds: z.array(z.uuid()).max(MAX_ALLOCATION_PEOPLE).default([]),
  })
  .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: 'The end must come after the start.',
    path: ['endsAt'],
  })
  .meta({ id: 'LinkContextAllocationRequest' });
export type LinkContextAllocationRequest = z.infer<typeof linkContextAllocationRequestSchema>;

// --------------------------------------------------------------- sjåførvelgeren (trinn 2, 2.5)

/**
 * HVEM KAN KJØRE — og svaret er «de som alt er på jobb da» (avgjørelse 7A).
 *
 * ================================================================================================
 * UTEN PERSONFELT-FILTERET, OG DET ER EN BESLUTNING OG IKKE EN FORENKLING.
 *
 * Spesifikasjonen §6: «Sjåfør velges blant personer med et personfelt festivalen har pekt ut
 * (f.eks. Førerkort) som er på skift i tidsrommet.» Personfeltene defineres i TRINN 3. Analysen
 * §4 merknad 2.5 sto overfor valget mellom å dra `person_fields` fram eller å levere velgeren uten
 * feltet, og anbefalte det siste: «det er brukbart alene, som §9 krever». Atle tok 7A.
 *
 * Så dette er halve filteret, med vilje, og den halvdelen som faktisk sparer tid: uten den er
 * alternativet hele årgangens stab i en nedtrekksliste. Når trinn 3 kommer, snevrer feltet inn den
 * samme lista — ingen skjerm trenger å endre form.
 *
 * ================================================================================================
 * `editionPersonId` OG IKKE `contactId`, fordi det er DET reisen lagrer.
 *
 * `performance_travel.driver_edition_person_id` peker på årgangens stabsrad, ikke på kontakten.
 * Det er den samme regelen `assertDriverExists` håndhever: «sjåfør velges fra egen stab eller
 * frivillige i arrangementets People-modul» — en vilkårlig kontakt ville latt artistens egen agent
 * bli satt til å kjøre bussen.
 */
export const allocationDriverDtoSchema = z
  .object({
    editionPersonId: z.uuid(),
    /**
     * Kontakten og personen bak stabsraden (trinn 1 §3). Personvelgeren lagrer sjåføren på
     * kontakten, og viser de som er på skift øverst — den kjenner dem igjen på disse.
     */
    contactId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    /** «Lydtekniker», «Sjåfør». Fritekst på årgangsraden, null når ingen har gitt dem en. */
    roleTitle: z.string().nullable(),
    /** Avdelingen de hører til i år. Null er «uten avdeling». */
    departmentName: z.string().nullable(),
    /**
     * SKIFTET SOM GJØR DEM TILGJENGELIGE, som én linje under navnet.
     *
     * Det er begrunnelsen for at de står på lista, og en liste som bare ga navn ville krevd at man
     * åpnet vaktplanen for å vite om personen faktisk er ledig eller står på scenen samtidig.
     * Har de flere skift i tidsrommet, er dette det første som begynner.
     */
    shiftTitle: z.string(),
    shiftStartsAt: z.iso.datetime(),
    shiftEndsAt: z.iso.datetime(),
  })
  .meta({ id: 'AllocationDriver' });
export type AllocationDriverDto = z.infer<typeof allocationDriverDtoSchema>;

/**
 * Ikke paginert, og det er en egenskap ved spørsmålet: «hvem er på skift mellom 15:30 og 17:00»
 * har et tosifret svar på en festival av enhver størrelse. `MAX_ALLOCATION_DRIVERS` er taket som
 * gjør det til en påstand framfor et håp.
 */
export const MAX_ALLOCATION_DRIVERS = 200;

export const listAllocationDriversQuerySchema = z
  .object({
    editionId: z.uuid(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((value) => Date.parse(value.to) > Date.parse(value.from), {
    message: 'The end must come after the start.',
    path: ['to'],
  })
  .meta({ id: 'ListAllocationDriversQuery' });
export type ListAllocationDriversQuery = z.infer<typeof listAllocationDriversQuerySchema>;

export const allocationDriverListResponseSchema = z
  .object({ items: z.array(allocationDriverDtoSchema) })
  .meta({ id: 'AllocationDriverListResponse' });
export type AllocationDriverListResponse = z.infer<typeof allocationDriverListResponseSchema>;

// -------------------------------------------- «Liste til kjøkkenet» (§7.3, trinn 4.3)

/**
 * SUMMERT FRA PERSONENE PÅ EN TILDELING.
 *
 * §7.3: «For typer med `summarize_person_fields`: en seksjon ‹Summert fra personene i tildelingene›
 * per dag (Måltid: allergier, kosthold, ‹uten registrerte felt›), med ‹Liste til kjøkkenet›.»
 *
 * ================================================================================================
 * TO NIVÅER, OG DET ANDRE ER DET SOM GJØR DEN BRUKBAR.
 *
 * `groups` er tallene et kjøkken leser: «3 vegetar, 1 nøtteallergi». `people` er lista de tar med
 * seg ut: ett navn per rad med de samme feltene. Et kjøkken trenger begge — summen for å bestille,
 * og navnene for å vite hvem som spør ved bordet.
 *
 * ================================================================================================
 * «UTEN REGISTRERTE FELT» ER EN EGEN GRUPPE, OG DEN ER POENGET.
 *
 * Et menneske ingen har spurt er ikke det samme som et menneske uten allergi, og et kjøkken som
 * bestiller for «3 vegetar av 20» må vite om de sytten er kjent eller ukjent. Gruppen har derfor en
 * tom `value` og telles for seg.
 */
export const allocationSummaryGroupSchema = z
  .object({
    /** Personfeltets nøkkel — «allergies», «diet». */
    fieldKey: z.string(),
    fieldLabel: z.string(),
    /** Standardfeltet gruppa hører til (0921). For «allergens_diets» er verdien en del av svaret, som skrevet (A4). */
    systemKey: personSystemKeySchema.nullable(),
    /** Verdien, som den står. Tom streng er «uten registrerte felt» — se filhodet. */
    value: z.string(),
    count: z.number().int(),
  })
  .meta({ id: 'AllocationSummaryGroup' });

export const allocationSummaryPersonSchema = z
  .object({
    contactId: z.uuid(),
    displayName: z.string(),
    /** Bare feltene typen summerer på. Resten av personens felt er ikke kjøkkenets sak. */
    values: z.record(z.string(), z.string()),
    /** «Allergier og kosthold» fra personen (`app.person_cards`), som tekst (A4), når den som spør har rett til det. */
    food: z.string().nullable(),
  })
  .meta({ id: 'AllocationSummaryPerson' });

export const allocationSummarySchema = z
  .object({
    allocationId: z.uuid(),
    /** Feltene typen summerer på, i typens egen rekkefølge. Tom: typen summerer ingenting. */
    fieldKeys: z.array(z.string()),
    groups: z.array(allocationSummaryGroupSchema),
    people: z.array(allocationSummaryPersonSchema),
    /**
     * HVOR FOLKENE KOM FRA (rettelsesordren B3, QA-funn 6).
     *
     * `allocation` er `allocation_people` — de festivalen selv satte på. `travellingParty` er
     * reservefallet: en middag knyttet fra advancen har ingen egne folk, og de som skal spise er
     * programpostens reisefølge. Lista sa «Ingen personer på tildelingen» om åtte mennesker.
     *
     * KILDEN STÅR I SVARET og utledes ikke av at lista er tom: «ingen er satt på» og «reisefølget
     * er tomt» er to tilstander, og et kjøkken som bestiller mat skal vite hvilken det er.
     */
    /*
     * `travelling_party` siden 26.10 (AI-ANALYSE §8.2: enumverdier er snake_case). Serveren sender
     * den nye; den gamle står i lista til nettlesere med en eldre bunt er borte (B1-rapporten).
     */
    source: z.enum(['allocation', 'travelling_party', 'travellingParty']),
    /** Programposten reisefølget hører til, når kilden er den. «Kvitbjørn». */
    performanceTitle: z.string().nullable(),
    /**
     * PÅRØRENDE ELLER FØDSELSDATO ER HOLDT TILBAKE (1077): typen summerer på et av dem, og den som
     * spør er under plattformens grense. Lista viser da resten, og sier hvorfor de mangler.
     */
    sensitiveHidden: z.boolean(),
  })
  .meta({ id: 'AllocationSummary' });
export type AllocationSummary = z.infer<typeof allocationSummarySchema>;

// ------------------------------------------------ tidslinja: mennesker og ting i ett (trinn 5.1)

/**
 * ÉN HENDELSE I ÉN BANE.
 *
 * `app.timeline_entries` gir én rad per (hendelse, menneske): en bil står i bilens bane OG i
 * sjåførens, et skift i hver av de tilordnedes. Leseren grupperer på `resourceId` for tingbanene
 * og på `personId` for menneskebanene, fra det samme svaret — se migrering 0209.
 *
 * `personId` null er ekte: en tildeling uten folk, og et skift ingen er satt på. Det siste er
 * nettopp «ubemannet», og en form som ikke kunne uttrykke det ville skjult hullet listen teller.
 */
export const timelineEntrySchema = z
  .object({
    /** `performance` kom i trinn 6.4: programposten lest som en tildeling av scenens ressurs. */
    source: z.enum(['allocation', 'shift', 'performance']),
    entryId: z.uuid(),
    resourceId: z.uuid().nullable(),
    resourceName: z.string().nullable(),
    personId: z.uuid().nullable(),
    personName: z.string().nullable(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    night: z.iso.date().nullable(),
    departmentId: z.uuid().nullable(),
    quantity: z.number().int(),
    /**
     * `planned` / `done` / `cancelled` for en tildeling; `assigned` / `confirmed` /
     * `declined` / `unassigned` for et skift. To enumer i én kolonne, med vilje: leseren tegner
     * en blokk og trenger én streng å style den etter, ikke to felt å velge mellom.
     */
    status: z.string(),
  })
  .meta({ id: 'TimelineEntry' });
export type TimelineEntry = z.infer<typeof timelineEntrySchema>;

export const listTimelineQuerySchema = z
  .object({
    editionId: z.uuid(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((value) => Date.parse(value.to) > Date.parse(value.from), {
    message: 'The end must come after the start.',
    path: ['to'],
  })
  .meta({ id: 'ListTimelineQuery' });
export type ListTimelineQuery = z.infer<typeof listTimelineQuerySchema>;

/**
 * Ikke paginert, og taket er en påstand framfor et håp — som sjåførlista.
 *
 * Et døgn på en stor festival er noen hundre skift og noen titalls tildelinger. Vinduet er
 * klientens, og en klient som ber om hele året får taket i ansiktet framfor et svar som tar et
 * minutt å tegne.
 */
export const MAX_TIMELINE_ENTRIES = 2000;

export const timelineResponseSchema = z
  .object({ items: z.array(timelineEntrySchema) })
  .meta({ id: 'TimelineResponse' });
export type TimelineResponse = z.infer<typeof timelineResponseSchema>;

/**
 * «ANGRE» PÅ «LØSNE» (sak 202). Tildelingen konteksten bar før den ble løsnet — kalleren har den
 * fra raden den nettopp viste. Tjenesten legger den tilbake med id-en og folkene den hadde, og
 * peker raden på den igjen. Ikke en ny bestilling: «Velg» på nytt ville mistet avdeling, notat og
 * hvem som var med.
 */
export const restoreContextAllocationRequestSchema = z
  .object({ allocationId: z.uuid() })
  .meta({ id: 'RestoreContextAllocationRequest' });
export type RestoreContextAllocationRequest = z.infer<typeof restoreContextAllocationRequestSchema>;
